<template>
  <br/>
  <div class="row">
    <div class="col-12 mb-3">
      <h4>New Creators</h4>
    </div>

    <div v-if="successMessage" class="col-12">
      <div class="alert alert-success" role="alert">{{ successMessage }}</div>
    </div>
    <div v-if="errorMessage" class="col-12">
      <div class="alert alert-danger" role="alert">{{ errorMessage }}</div>
    </div>
  </div>

  <section class="card mb-4" aria-labelledby="add-new-creator-title">
    <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
      <h5 id="add-new-creator-title" class="mb-0">Add creator to feed</h5>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :aria-expanded="showAddCreatorSection"
        aria-controls="add-new-creator-content"
        @click="showAddCreatorSection = !showAddCreatorSection"
      >
        {{ showAddCreatorSection ? 'Hide' : 'Show' }}
      </button>
    </div>
    <div v-show="showAddCreatorSection" id="add-new-creator-content" class="card-body">
      <p class="text-muted mt-0">
        Search all creators and include one manually. The creator will appear in the feed immediately.
      </p>

      <form class="row g-3 align-items-end" @submit.prevent="searchCreatorsToAdd">
        <div class="col-lg-8 col-md-7 col-12">
          <label for="add-new-creator-search" class="form-label">Creator or channel</label>
          <input
            id="add-new-creator-search"
            v-model="addCreatorKeyword"
            class="form-control"
            type="search"
            placeholder="Name, slug, email or description"
            autocomplete="off"
          />
        </div>
        <div class="col-lg-4 col-md-5 col-12">
          <button
            type="submit"
            class="btn btn-primary w-100"
            :disabled="searchingCreators || addCreatorKeyword.trim().length < 2"
          >
            <span v-if="!searchingCreators">Search creators</span>
            <font-awesome-icon v-else icon="fa-solid fa-sync" spin/>
          </button>
        </div>
      </form>

      <div v-if="addCreatorError" class="alert alert-danger mt-3 mb-0" role="alert">
        {{ addCreatorError }}
      </div>

      <div v-if="addCreatorResults.length > 0" class="list-group mt-3">
        <div
          v-for="creator in addCreatorResults"
          :key="creator.id"
          class="list-group-item d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3"
        >
          <div class="d-flex flex-column align-items-start gap-1">
            <a
              v-if="isSafeUrl(creator.link)"
              :href="creator.link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <strong>{{ valueOrPlaceholder(creator.name) }}</strong>
            </a>
            <strong v-else>{{ valueOrPlaceholder(creator.name) }}</strong>
            <span class="text-muted small">
              {{ valueOrPlaceholder(creator.slug) }} · {{ valueOrPlaceholder(creator.email) }}
            </span>
            <span :class="pageStatusBadgeClass(creator.status)">
              {{ valueOrPlaceholder(creator.status_name) }}
            </span>
          </div>
          <button
            type="button"
            class="btn btn-success text-white flex-shrink-0"
            :disabled="addingChannelId !== null || creator.is_manually_included"
            @click="manuallyIncludeCreator(creator)"
          >
            <span v-if="creator.is_manually_included">Already added</span>
            <span v-else-if="addingChannelId !== creator.id">Add to feed</span>
            <font-awesome-icon v-else icon="fa-solid fa-sync" spin/>
          </button>
        </div>
      </div>

      <p v-else-if="addCreatorSearchPerformed && !searchingCreators" class="text-muted mt-3 mb-0">
        No creators found.
      </p>
    </div>
  </section>

  <section class="card mb-4" aria-labelledby="new-creators-filters-title">
    <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
      <div class="d-flex flex-wrap align-items-center gap-2">
        <h5 id="new-creators-filters-title" class="mb-0">Filters</h5>
        <span v-if="hasActiveFilters" class="badge badge-info">Active filters</span>
      </div>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :aria-expanded="showFiltersSection"
        aria-controls="new-creators-filters-content"
        @click="showFiltersSection = !showFiltersSection"
      >
        {{ showFiltersSection ? 'Hide' : 'Show' }}
      </button>
    </div>

    <div v-show="showFiltersSection" id="new-creators-filters-content" class="card-body">
      <form class="row" @submit.prevent="applyFilters">
        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-search" class="form-label">Creator or channel</label>
          <input
            id="new-creators-search"
            v-model="filters.keyword"
            class="form-control"
            type="search"
            placeholder="Name, slug, email or description"
          />
        </div>

        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-country" class="form-label">Creator country</label>
          <select id="new-creators-country" v-model="filters.country" class="form-control">
            <option value="">All</option>
            <option v-for="country in countries" :key="country.value" :value="country.value">
              {{ country.label || country.name || country.value }}
            </option>
          </select>
        </div>

        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-status" class="form-label">Creator status</label>
          <select id="new-creators-status" v-model="filters.creatorStatus" class="form-control">
            <option value="">All</option>
            <option v-for="status in creatorStatuses" :key="status.value" :value="status.value">
              {{ status.name }}
            </option>
          </select>
        </div>

        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-category" class="form-label">Creator category</label>
          <select id="new-creators-category" v-model="filters.categoryId" class="form-control">
            <option value="">All</option>
            <option v-for="category in contentCategories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
        </div>

        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-language" class="form-label">Content language</label>
          <select id="new-creators-language" v-model="filters.contentLanguage" class="form-control">
            <option value="">All</option>
            <option v-for="language in contentLanguages" :key="language.value" :value="language.value">
              {{ language.label }}
            </option>
          </select>
        </div>

        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-feed-status" class="form-label">Feed status</label>
          <select id="new-creators-feed-status" v-model="filters.feedStatus" class="form-control">
            <option value="">All</option>
            <option value="included">Included</option>
            <option value="excluded">Excluded</option>
          </select>
        </div>

        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-state" class="form-label">Management state</label>
          <select id="new-creators-state" v-model="filters.managementState" class="form-control">
            <option value="">All</option>
            <option v-for="state in managementStates" :key="state.value" :value="state.value">
              {{ state.label }}
            </option>
          </select>
        </div>

        <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
          <label for="new-creators-per-page" class="form-label">Per page</label>
          <select id="new-creators-per-page" v-model="perPage" class="form-control" @change="changePerPage">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>

        <div class="col-12 d-flex flex-wrap gap-2">
          <button type="submit" class="btn btn-primary" :disabled="loading">Apply filters</button>
          <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="clearFilters">
            Clear filters
          </button>
          <button
            type="button"
            class="btn btn-success text-white"
            aria-label="Export new creators as CSV"
            :disabled="loading || exporting"
            @click="exportNewCreators"
          >
            <span v-if="!exporting">Export CSV</span>
            <font-awesome-icon v-else icon="fa-solid fa-sync" spin/>
          </button>
        </div>
      </form>
    </div>
  </section>

  <div class="d-none d-md-block table-responsive">
    <table class="table table-hover align-middle new-creators-table">
      <thead>
      <tr>
        <th scope="col">Creator</th>
        <th scope="col">Page status</th>
        <th scope="col">Registration date</th>
        <th scope="col">Feed status</th>
        <th scope="col">Inclusion source</th>
        <th scope="col">Management state</th>
        <th scope="col" class="management-action-cell">Manage</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="loading">
        <td colspan="7" class="text-center"><font-awesome-icon icon="fa-solid fa-sync" spin/></td>
      </tr>
      <tr v-for="creator in creators" v-else-if="creators.length > 0" :key="creator.id">
        <td>
          <a v-if="isSafeUrl(creator.creator_link)" :href="creator.creator_link" target="_blank" rel="noopener noreferrer">
            {{ valueOrPlaceholder(creator.creator_name) }}
          </a>
          <span v-else>{{ valueOrPlaceholder(creator.creator_name) }}</span>
        </td>
        <td><span :class="pageStatusBadgeClass(creator.page_status)">{{ creator.page_status_label }}</span></td>
        <td class="text-nowrap">{{ formatDateTime(creator.registration_date) }}</td>
        <td><span :class="feedStatusBadgeClass(creator.feed_status)">{{ capitalize(creator.feed_status) }}</span></td>
        <td>{{ capitalize(creator.inclusion_source) }}</td>
        <td>
          <div class="d-flex flex-column align-items-start gap-1">
            <span :class="managementStateBadgeClass(creator.management_state)">
              {{ creator.management_state_label }}
            </span>
            <small v-if="creator.management_updated_at" class="text-muted">
              {{ valueOrPlaceholder(creator.management_updated_by) }} · {{ formatDateTime(creator.management_updated_at) }}
            </small>
          </div>
        </td>
        <td class="management-action-cell">
          <select
            class="form-select form-select-sm"
            :value="creator.management_state"
            :disabled="changingCreatorId === creator.id"
            :aria-label="`Management state for ${creator.creator_name}`"
            @change="updateManagementState(creator, $event)"
          >
            <option
              v-for="state in managementStates"
              :key="state.value"
              :value="state.value"
              :disabled="isManagementStateDisabled(creator, state)"
            >
              {{ state.label }}
            </option>
          </select>
        </td>
      </tr>
      <tr v-else>
        <td colspan="7" class="text-center">No new creators found</td>
      </tr>
      </tbody>
    </table>
  </div>

  <div class="d-md-none d-flex flex-column gap-3">
    <div v-if="loading" class="text-center py-4"><font-awesome-icon icon="fa-solid fa-sync" spin/></div>
    <article v-for="creator in creators" v-else-if="creators.length > 0" :key="creator.id" class="card creator-management-card">
      <div class="card-body d-flex flex-column gap-3">
        <div class="d-flex justify-content-between align-items-start gap-2">
          <div>
            <a v-if="isSafeUrl(creator.creator_link)" :href="creator.creator_link" target="_blank" rel="noopener noreferrer">
              <strong>{{ valueOrPlaceholder(creator.creator_name) }}</strong>
            </a>
            <strong v-else>{{ valueOrPlaceholder(creator.creator_name) }}</strong>
            <div class="text-muted small">Registered {{ formatDateTime(creator.registration_date) }}</div>
          </div>
          <span :class="feedStatusBadgeClass(creator.feed_status)">{{ capitalize(creator.feed_status) }}</span>
        </div>

        <dl class="row mb-0 small">
          <dt class="col-5">Page status</dt>
          <dd class="col-7"><span :class="pageStatusBadgeClass(creator.page_status)">{{ creator.page_status_label }}</span></dd>
          <dt class="col-5">Source</dt>
          <dd class="col-7">{{ capitalize(creator.inclusion_source) }}</dd>
          <dt class="col-5">Management</dt>
          <dd class="col-7 mb-0">
            <span :class="managementStateBadgeClass(creator.management_state)">{{ creator.management_state_label }}</span>
          </dd>
        </dl>

        <small v-if="creator.management_updated_at" class="text-muted">
          Last changed by {{ valueOrPlaceholder(creator.management_updated_by) }} on
          {{ formatDateTime(creator.management_updated_at) }}
        </small>

        <div>
          <label :for="`creator-management-${creator.id}`" class="form-label">Management state</label>
          <select
            :id="`creator-management-${creator.id}`"
            class="form-select"
            :value="creator.management_state"
            :disabled="changingCreatorId === creator.id"
            @change="updateManagementState(creator, $event)"
          >
            <option
              v-for="state in managementStates"
              :key="state.value"
              :value="state.value"
              :disabled="isManagementStateDisabled(creator, state)"
            >
              {{ state.label }}
            </option>
          </select>
        </div>
      </div>
    </article>
    <p v-else class="text-center py-4 mb-0">No new creators found</p>
  </div>

  <div v-if="totalRecords >= 1" class="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
    <p class="mb-0">Total creators: {{ totalRecords }}</p>
    <nav v-if="totalPages >= 1" aria-label="New creators pagination">
      <ul class="pagination mb-0">
        <li class="page-item" :class="{disabled: currentPage <= 1}">
          <button type="button" class="page-link" :disabled="currentPage <= 1" @click="paginate(currentPage - 1)">
            Previous
          </button>
        </li>
        <li class="page-item disabled"><span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span></li>
        <li class="page-item" :class="{disabled: currentPage >= totalPages}">
          <button type="button" class="page-link" :disabled="currentPage >= totalPages" @click="paginate(currentPage + 1)">
            Next
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import axios from 'axios';

function emptyFilters() {
  return {
    keyword: '',
    country: '',
    categoryId: '',
    contentLanguage: '',
    creatorStatus: '',
    feedStatus: '',
    managementState: '',
  };
}

export default {
  data() {
    return {
      creators: [],
      filters: emptyFilters(),
      activeFilters: emptyFilters(),
      countries: [],
      contentCategories: [],
      contentLanguages: [],
      creatorStatuses: [],
      addCreatorKeyword: '',
      addCreatorResults: [],
      addCreatorSearchPerformed: false,
      addCreatorError: '',
      searchingCreators: false,
      addingChannelId: null,
      addCreatorSearchRequestId: 0,
      showAddCreatorSection: false,
      showFiltersSection: false,
      managementStates: [
        {value: 'automatic', label: 'Automatic'},
        {value: 'manually_included', label: 'Manually included'},
        {value: 'manually_excluded', label: 'Manually excluded'},
      ],
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      perPage: 20,
      loading: true,
      changingCreatorId: null,
      exporting: false,
      successMessage: '',
      errorMessage: '',
      requestId: 0,
    };
  },

  computed: {
    hasActiveFilters() {
      return Object.values(this.activeFilters).some(value => value !== '');
    },
  },

  mounted() {
    this.loadFilterOptions();
    this.fetchCreators();
  },

  methods: {
    async searchCreatorsToAdd() {
      const keyword = this.addCreatorKeyword.trim();

      if (keyword.length < 2) {
        this.addCreatorResults = [];
        this.addCreatorSearchPerformed = false;
        this.addCreatorError = 'Enter at least two characters to search.';
        return;
      }

      const currentRequestId = ++this.addCreatorSearchRequestId;
      this.searchingCreators = true;
      this.addCreatorError = '';
      this.addCreatorSearchPerformed = true;

      try {
        const response = await axios.get('/admin/new-channels/candidates', {
          params: {
            page: 1,
            per_page: 10,
            keyword,
          },
        });

        if (currentRequestId !== this.addCreatorSearchRequestId) {
          return;
        }

        const payload = response.data || {};
        this.addCreatorResults = Array.isArray(payload.data) ? payload.data : [];
      } catch (error) {
        if (currentRequestId !== this.addCreatorSearchRequestId) {
          return;
        }

        this.addCreatorResults = [];
        this.addCreatorError = this.responseErrorMessage(error, 'Failed to search creators.');
      } finally {
        if (currentRequestId === this.addCreatorSearchRequestId) {
          this.searchingCreators = false;
        }
      }
    },

    async manuallyIncludeCreator(creator) {
      this.addingChannelId = creator.id;
      this.successMessage = '';
      this.errorMessage = '';
      this.addCreatorError = '';

      try {
        await axios.post('/admin/new-channels', {channel_id: creator.id});

        this.successMessage = `${creator.name || 'Creator'} was added to the New Creators feed manually.`;
        this.addCreatorKeyword = '';
        this.addCreatorResults = [];
        this.addCreatorSearchPerformed = false;
        this.showAddCreatorSection = false;
        this.currentPage = 1;
        await this.fetchCreators(1);
      } catch (error) {
        this.addCreatorError = this.responseErrorMessage(error, 'Failed to add the creator to the feed.');
      } finally {
        this.addingChannelId = null;
      }
    },

    async loadFilterOptions() {
      const requests = await Promise.allSettled([
        axios.get('/admin/countries'),
        axios.get('/admin/content-categories', {params: {per_page: 100}}),
        axios.get('/admin/languages'),
        axios.get('/admin/channel/statuses'),
      ]);

      const countriesResponse = requests[0];
      if (countriesResponse.status === 'fulfilled') {
        const countries = countriesResponse.value.data;
        this.countries = Array.isArray(countries) ? countries : [];
      }

      const categoriesResponse = requests[1];
      if (categoriesResponse.status === 'fulfilled') {
        const payload = categoriesResponse.value.data;
        this.contentCategories = Array.isArray(payload) ? payload : (payload.data || []);
      }

      const languagesResponse = requests[2];
      if (languagesResponse.status === 'fulfilled') {
        const languages = languagesResponse.value.data || {};
        this.contentLanguages = Object.entries(languages).map(([value, language]) => ({
          value,
          label: language.name || language.native || value,
        }));
      }

      const statusesResponse = requests[3];
      if (statusesResponse.status === 'fulfilled') {
        const statuses = statusesResponse.value.data && statusesResponse.value.data.status;
        this.creatorStatuses = Array.isArray(statuses) ? statuses : [];
      }
    },

    buildFilterParams() {
      const params = {};
      const filterMap = {
        keyword: 'keyword',
        country: 'country',
        categoryId: 'category_id',
        contentLanguage: 'content_language',
        creatorStatus: 'creator_status',
        feedStatus: 'feed_status',
        managementState: 'management_state',
      };

      Object.entries(filterMap).forEach(([localName, apiName]) => {
        if (this.activeFilters[localName] !== '') {
          params[apiName] = this.activeFilters[localName];
        }
      });

      return params;
    },

    buildListParams(page) {
      return {
        ...this.buildFilterParams(),
        page,
        per_page: this.perPage,
      };
    },

    async fetchCreators(page = this.currentPage) {
      const currentRequestId = ++this.requestId;
      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await axios.get('/admin/new-channels', {params: this.buildListParams(page)});

        if (currentRequestId !== this.requestId) {
          return;
        }

        const payload = response.data || {};
        const totalPages = Number(payload.totalPages || 0);

        if (page > totalPages && totalPages >= 1) {
          this.currentPage = totalPages;
          await this.fetchCreators(totalPages);
          return;
        }

        this.creators = Array.isArray(payload.data) ? payload.data : [];
        this.currentPage = Number(payload.pageNumber || page);
        this.totalPages = totalPages;
        this.totalRecords = Number(payload.totalRecords || 0);
      } catch (error) {
        if (currentRequestId !== this.requestId) {
          return;
        }

        this.creators = [];
        this.totalPages = 0;
        this.totalRecords = 0;
        this.errorMessage = this.responseErrorMessage(error, 'Failed to load new creators.');
      } finally {
        if (currentRequestId === this.requestId) {
          this.loading = false;
        }
      }
    },

    applyFilters() {
      this.activeFilters = {...this.filters};
      this.showFiltersSection = false;
      this.currentPage = 1;
      this.fetchCreators(1);
    },

    clearFilters() {
      this.filters = emptyFilters();
      this.activeFilters = emptyFilters();
      this.showFiltersSection = false;
      this.currentPage = 1;
      this.fetchCreators(1);
    },

    changePerPage() {
      this.currentPage = 1;
      this.fetchCreators(1);
    },

    paginate(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }

      this.currentPage = page;
      this.fetchCreators(page);
    },

    async exportNewCreators() {
      this.exporting = true;
      this.errorMessage = '';

      try {
        const response = await axios.get('/admin/export/new-channels', {
          params: this.buildFilterParams(),
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8'});
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const timestamp = new Date().toISOString()
          .replace(/[:.]/g, '_')
          .replace('T', '_')
          .replace('Z', '');

        link.href = objectUrl;
        link.download = `new_creators_${timestamp}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);

        this.$toast.success('New creators exported successfully!');
      } catch (error) {
        this.errorMessage = this.responseErrorMessage(error, 'Failed to export new creators.');
        this.$toast.error(this.errorMessage);
      } finally {
        this.exporting = false;
      }
    },

    async updateManagementState(creator, event) {
      const managementState = event.target.value;
      event.target.value = creator.management_state;

      if (managementState === creator.management_state) {
        return;
      }

      this.changingCreatorId = creator.id;
      this.successMessage = '';
      this.errorMessage = '';

      try {
        const response = await axios.patch(`/admin/new-channels/${creator.id}/management-state`, {
          management_state: managementState,
        });
        const updatedCreator = response.data.data || response.data;

        if (this.hasActiveFilters) {
          await this.fetchCreators();
        } else {
          const creatorIndex = this.creators.findIndex(item => item.id === creator.id);

          if (creatorIndex !== -1) {
            this.creators.splice(creatorIndex, 1, updatedCreator);
          }
        }

        this.successMessage = `${creator.creator_name} is now ${updatedCreator.management_state_label}.`;
      } catch (error) {
        this.errorMessage = this.responseErrorMessage(error, 'Failed to update the creator management state.');
      } finally {
        this.changingCreatorId = null;
      }
    },

    isManagementStateDisabled(creator, state) {
      return state.value === 'manually_included' && creator.page_status !== 'OK';
    },

    responseErrorMessage(error, fallback) {
      return error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback;
    },

    isSafeUrl(url) {
      if (!url || typeof url !== 'string') {
        return false;
      }

      try {
        const {protocol} = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
      } catch {
        return false;
      }
    },

    valueOrPlaceholder(value) {
      return value !== null && value !== undefined && value !== '' ? value : '-';
    },

    formatDateTime(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },

    capitalize(value) {
      return value ? value.charAt(0).toUpperCase() + value.slice(1) : '-';
    },

    pageStatusBadgeClass(status) {
      const classes = {
        OK: 'badge badge-success',
        denied: 'badge badge-danger',
        paused: 'badge badge-warning',
        awaiting_confirmation: 'badge badge-info',
        pending_check: 'badge badge-info',
        stripe_confirmed_not_published: 'badge badge-info',
        ipay_confirmed_not_published: 'badge badge-info',
      };

      return classes[status] || 'badge badge-secondary';
    },

    feedStatusBadgeClass(status) {
      return status === 'included' ? 'badge badge-success' : 'badge badge-secondary';
    },

    managementStateBadgeClass(state) {
      const classes = {
        automatic: 'badge badge-info',
        manually_included: 'badge badge-success',
        manually_excluded: 'badge badge-danger',
      };

      return classes[state] || 'badge badge-secondary';
    },
  },
};
</script>

<style scoped>
.new-creators-table {
  min-width: 72rem;
}

.management-action-cell {
  position: sticky;
  right: 0;
  z-index: 2;
  min-width: 12rem;
  background-color: var(--bs-body-bg, #fff);
  box-shadow: -6px 0 12px -6px rgba(0, 0, 0, 0.15);
}

.new-creators-table thead .management-action-cell {
  z-index: 3;
}

.creator-management-card {
  border-color: rgba(0, 0, 0, 0.12);
}
</style>
